import { assumedConsentMethod } from "./defaults";
import type { CsvValidationResult, UploadRow } from "./types";

export async function parseUploadFile(file: File, attestedBy: string, allowTestNumbers = false): Promise<CsvValidationResult> {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  let candidates: string[];

  if (ext === "xlsx" || ext === "xls") {
    candidates = await parseExcelFile(file);
  } else if (ext === "csv") {
    const text = await file.text();
    candidates = parseCsvText(text);
  } else {
    const text = await file.text();
    candidates = parseTextLines(text);
  }

  return buildValidationResult(candidates, attestedBy, allowTestNumbers);
}

function parseTextLines(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function parseCsvText(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => splitCsvLine(line)[0]?.trim() ?? "")
    .filter(Boolean);
}

async function parseExcelFile(file: File): Promise<string[]> {
  const { read, utils } = await import("xlsx");
  const buffer = await file.arrayBuffer();
  const workbook = read(buffer, { type: "array" });
  const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = utils.sheet_to_json<unknown[]>(firstSheet, { header: 1 });
  return rows
    .map((row) => String((row as unknown[])[0] ?? "").trim())
    .filter(Boolean);
}

function buildValidationResult(candidates: string[], attestedBy: string, allowTestNumbers = false): CsvValidationResult {
  const consentReceivedAt = new Date().toISOString();
  const seen = new Set<string>();
  const rows: UploadRow[] = [];

  for (const [index, candidate] of candidates.entries()) {
    const normalized = normalizePhoneNumber(candidate, allowTestNumbers);
    let status: UploadRow["status"] = "valid";
    let rejectionReason: string | null = null;

    if (!normalized) {
      status = "rejected_invalid";
      rejectionReason = "Not a recognizable phone number.";
    } else if (seen.has(normalized)) {
      status = "rejected_duplicate";
      rejectionReason = "Duplicate phone number in this upload.";
    } else {
      seen.add(normalized);
    }

    rows.push({
      row_number: index + 1,
      phone_number: candidate,
      normalized_phone_number: normalized,
      consent_received_at: consentReceivedAt,
      consent_method: assumedConsentMethod,
      consent_attested_by: attestedBy,
      status,
      rejection_reason: rejectionReason,
    });
  }

  const validRows = rows.filter((r) => r.status === "valid").length;

  return {
    summary: {
      totalRows: rows.length,
      validRows,
      rejectedRows: rows.length - validRows,
    },
    rows,
  };
}

function splitCsvLine(line: string): string[] {
  const values: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (char === "," && !inQuotes) {
      values.push(current);
      current = "";
      continue;
    }
    current += char;
  }
  values.push(current);
  return values;
}

export function parsePhoneListText(text: string, attestedBy: string, allowTestNumbers = false): CsvValidationResult {
  const candidates = text
    .split(/[\r\n,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return buildValidationResult(candidates, attestedBy, allowTestNumbers);
}

export function normalizePhoneNumber(value: string, allowTestNumbers = false): string | null {
  const digits = value.replace(/\D/g, "");
  if (allowTestNumbers && digits.length === 10 && digits.startsWith("999")) return `+1${digits}`;
  if (allowTestNumbers && digits.length === 11 && digits.startsWith("1999")) return `+${digits}`;
  if (digits.length === 10 && digits.startsWith("999")) return null;
  if (digits.length === 11 && digits.startsWith("1999")) return null;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}
